<template>
  <div ref="containerRef" :class="[isMobile ? 'iframe-wrapper mobile' : 'iframe-wrapper', { 'css-fullscreen': isCssFullscreen }]">
    <button @click="toggleFullscreen" :class="fullScreenBtnClass" :title="isFullscreen || isCssFullscreen ? '退出全螢幕' : '進入全螢幕'">
      <i :class="isFullscreen || isCssFullscreen ? 'bi bi-fullscreen-exit' : 'bi bi-arrows-fullscreen'"></i>
    </button>
    <iframe
      :src="src"
      :class="[isMobile ? 'iframe-content mobile' : 'iframe-content', { 'css-fullscreen': isCssFullscreen }]"
      ref="iframe"
      :style="{ '--scale': scale }"
      allowfullscreen
    ></iframe>
  </div>
</template>

<script setup>
import { ref, defineProps, onMounted, onBeforeUnmount, computed } from 'vue';
import { useStore } from 'vuex';

const store = useStore();

defineProps({
  src: String,
});

const scale = ref(1);
const containerRef = ref(null);
const isFullscreen = ref(false);
const isCssFullscreen = ref(false);

const isMobile = computed(() => store.state.isMobile);

const fullScreenBtnClass = computed(() => {
  const isFullscreenActive = isFullscreen.value || isCssFullscreen.value;
  return isMobile.value ? (isFullscreenActive ? 'fullscreen-btn mobile fullscreen' : 'fullscreen-btn mobile') : isFullscreenActive ? 'fullscreen-btn fullscreen' : 'fullscreen-btn';
});

function updateScale() {
  if (containerRef.value && !isMobile.value && !isCssFullscreen.value) {
    const containerWidth = containerRef.value.offsetWidth;
    scale.value = containerWidth / 1920;
  } else if (containerRef.value && !isCssFullscreen.value) {
    const containerWidth = containerRef.value.offsetWidth;
    scale.value = containerWidth / 375;
  } else if (isCssFullscreen.value) {
    // CSS 全螢幕時的縮放計算
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;

    if (isMobile.value) {
      const scaleX = screenWidth / 375;
      const scaleY = screenHeight / 667;
      scale.value = Math.min(scaleX, scaleY);
    } else {
      const scaleX = screenWidth / 1920;
      const scaleY = screenHeight / 1080;
      scale.value = Math.min(scaleX, scaleY);
    }
  }
}

const toggleFullscreen = async () => {
  const supportsNativeFullscreen = !!(document.fullscreenEnabled || document.webkitFullscreenEnabled || document.mozFullScreenEnabled || document.msFullscreenEnabled);

  if (supportsNativeFullscreen && !isMobile.value) {
    // 桌面版使用原生 API
    try {
      if (!document.fullscreenElement) {
        if (containerRef.value.requestFullscreen) {
          await containerRef.value.requestFullscreen();
        } else if (containerRef.value.webkitRequestFullscreen) {
          await containerRef.value.webkitRequestFullscreen();
        }

        isFullscreen.value = true;
      } else {
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          await document.webkitExitFullscreen();
        }

        isFullscreen.value = false;
      }
    } catch (error) {
      console.error('原生全螢幕失敗，使用 CSS 替代:', error);
      toggleCssFullscreen();
    }
  } else {
    // 手機版或不支援原生 API 時使用 CSS 全螢幕
    toggleCssFullscreen();
  }
};

const toggleCssFullscreen = () => {
  isCssFullscreen.value = !isCssFullscreen.value;

  // 阻止頁面滾動
  if (isCssFullscreen.value) {
    document.body.style.overflow = 'hidden';
  } else {
    document.body.style.overflow = '';
  }

  setTimeout(updateScale, 100);
};

const handleFullscreenChange = () => {
  const isCurrentlyFullscreen = !!(document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement);

  // 全螢幕狀態改變時重新計算縮放
  isFullscreen.value = isCurrentlyFullscreen;
  setTimeout(updateScale, 500);
};

onMounted(() => {
  updateScale();
  window.addEventListener('resize', updateScale);

  // 支援不同瀏覽器的事件監聽
  document.addEventListener('fullscreenchange', handleFullscreenChange);
  document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
  document.addEventListener('mozfullscreenchange', handleFullscreenChange);
  document.addEventListener('msfullscreenchange', handleFullscreenChange);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateScale);
  document.removeEventListener('fullscreenchange', handleFullscreenChange);
  document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
  document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
  document.removeEventListener('msfullscreenchange', handleFullscreenChange);
});
</script>

<style scoped>
.iframe-wrapper {
  width: 70%;
  aspect-ratio: 16 / 9;
  position: relative;
  overflow: hidden;
  margin: 0 auto;
}

.iframe-wrapper.mobile {
  width: 80%;
  aspect-ratio: 10 / 18;
  max-width: 375px;
}

.iframe-wrapper.fullscreen {
  width: 100%;
  height: 100vh;
  aspect-ratio: unset;
  margin: 0;
}

.iframe-wrapper.css-fullscreen {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 9999;
  background: black;
  aspect-ratio: unset;
  margin: 0;
  max-width: none;
}

.iframe-content {
  width: 1920px;
  height: 1080px;
  transform-origin: center top;
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%) scale(var(--scale, 1));
  border: none;
}

.iframe-content.mobile {
  width: 375px;
  height: 667px;
  transform: translateX(-50%) scale(var(--scale, 1));
}

.iframe-content.css-fullscreen {
  transform-origin: center center;
  top: 50%;
  transform: translateX(-50%) translateY(-50%) scale(var(--scale, 1));
}

.fullscreen-btn {
  position: absolute;
  top: 40px;
  right: 10px;
  z-index: 10;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 10px;
  cursor: pointer;
  font-size: 16px;
  transition: background-color 0.3s ease;
}

.fullscreen-btn.fullscreen {
  top: 55px;
  right: 20px;
}

.fullscreen-btn.mobile {
  top: 5px;
  right: 5px;
  z-index: 10000;
}

.fullscreen-btn:hover {
  background: rgba(0, 0, 0, 0.9);
}

/* 全螢幕時調整按鈕位置 */
.iframe-wrapper.fullscreen .fullscreen-btn {
  position: fixed;
  top: 20px;
  right: 20px;
  background: rgba(0, 0, 0, 0.8);
  padding: 12px 14px;
  font-size: 18px;
}
</style>
